import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Save, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { contentAPI } from '../../services/api';

const Field = ({ label, children }) => (
  <div>
    <label className="text-sm font-medium text-gray-700 mb-1 block">{label}</label>
    {children}
  </div>
);

const AboutContentCard = ({ data, onChange }) => (
  <Card>
    <CardHeader><CardTitle>About Page Content</CardTitle></CardHeader>
    <CardContent className="space-y-4">
      <Field label="Introduction"><Textarea rows={4} value={data.intro || ''} onChange={e => onChange('intro', e.target.value)} data-testid="about-intro" /></Field>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Vision"><Textarea rows={2} value={data.vision || ''} onChange={e => onChange('vision', e.target.value)} /></Field>
        <Field label="Mission"><Textarea rows={2} value={data.mission || ''} onChange={e => onChange('mission', e.target.value)} /></Field>
      </div>
      <Field label="Hero Image URL">
        <Input value={data.heroImage || ''} onChange={e => onChange('heroImage', e.target.value)} />
        {data.heroImage && <img src={data.heroImage} alt="Preview" className="mt-2 h-32 w-full object-cover rounded-lg" />}
      </Field>
    </CardContent>
  </Card>
);

const MilestonesCard = ({ milestones, onChange }) => {
  const update = (idx, field, value) => onChange(milestones.map((m, i) => (i === idx ? { ...m, [field]: value } : m)));
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>Milestones</CardTitle>
        <Button size="sm" onClick={() => onChange([...milestones, { year: '', title: '', description: '' }])}><Plus className="w-4 h-4 mr-1" /> Add</Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {milestones.map((m, idx) => (
          <div key={`milestone-${idx}`} className="flex gap-3 items-start p-3 bg-gray-50 rounded-lg">
            <div className="grid grid-cols-3 gap-3 flex-1">
              <Input placeholder="Year" value={m.year || ''} onChange={e => update(idx, 'year', e.target.value)} />
              <Input placeholder="Title" value={m.title || ''} onChange={e => update(idx, 'title', e.target.value)} />
              <Input placeholder="Description" value={m.description || ''} onChange={e => update(idx, 'description', e.target.value)} />
            </div>
            <Button size="sm" variant="destructive" onClick={() => onChange(milestones.filter((_, i) => i !== idx))}><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

const AboutEditor = () => {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try { const res = await contentAPI.get('about'); setData(res.data || {}); }
    catch { toast.error('Failed to load'); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try { await contentAPI.update('about', data); toast.success('About page saved!'); }
    catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  if (!data) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  return (
    <div className="space-y-6 max-w-4xl">
      <AboutContentCard data={data} onChange={(field, value) => setData(prev => ({ ...prev, [field]: value }))} />
      <MilestonesCard milestones={data.milestones || []} onChange={milestones => setData(prev => ({ ...prev, milestones }))} />
      <Button onClick={save} disabled={saving} className="w-full" data-testid="save-about">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save About Page
      </Button>
    </div>
  );
};

export default AboutEditor;
